import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { loginSuccess } from '../../slices/loginSlice'
import {
  setUserId,
  setUserRole,
  setCompanyId,
  setUserMail,
  setCognitoId,
  setUserAdmin,
  setSymphonyUserId,
  clearUserState,
} from '../../slices/authUserSlice'
import storage from '../../utils/storage'
import ClipLoader from 'react-spinners/ClipLoader'
import { axios } from '../../lib/axios'

const LoginToken = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { userId } = useAppSelector((state) => state.authUser)

  useEffect(() => {
    const loginToken = searchParams.get('token')
    const email = searchParams.get('email')
    const redirectUrl = searchParams.get('redirectUrl') || '/'

    const currentUserId = userId || storage.getUserId()
    const currentUserMail = storage.getUserMail()
    const currentRefreshToken = storage.getRefreshToken()

    if (!email) {
      if (currentUserId && currentUserMail && currentRefreshToken) {
        // User is logged in, redirect to intended destination
        navigate(redirectUrl)
      } else {
        // User is not logged in, redirect to login
        navigate('/login')
      }
      return
    }

    // If already logged in with same email, redirect directly
    if (currentUserId && currentUserMail && currentRefreshToken) {
      if (currentUserMail.toLowerCase() === email.toLowerCase()) {
        navigate(redirectUrl)
        return
      } else {
        // Different email, clear storage
        storage.clearData()
        dispatch(clearUserState())
      }
    }

    // If no login token provided, redirect to login
    if (!loginToken) {
      const loginUrl =
        redirectUrl !== '/'
          ? `/login?email=${encodeURIComponent(
              email!,
            )}&redirectUrl=${encodeURIComponent(redirectUrl)}`
          : `/login?email=${encodeURIComponent(email!)}`
      navigate(loginUrl)
      return
    }

    // Exchange login token for access token
    async function exchangeLoginToken() {
      try {
        storage.clearData()
        dispatch(clearUserState())

        const response = await axios.post('/validate-login-token', {
          token: loginToken!,
        })

        storage.setToken(response.data.AccessToken)
        storage.setRefreshToken(response.data.RefreshToken)
        storage.setUserMail(email!)
        dispatch(setUserMail(email!))

        if (response.data.userId) {
          storage.setUserId(response.data.userId)
          dispatch(setUserId(response.data.userId))
        }
        if (response.data.userRole) {
          storage.setUserRole(response.data.userRole)
          dispatch(setUserRole(response.data.userRole))
        }
        if (response.data.companyId) {
          storage.setCompanyId(response.data.companyId)
          dispatch(setCompanyId(response.data.companyId))
        }
        if (response.data.cognitoId) {
          storage.setCognitoId(response.data.cognitoId)
          dispatch(setCognitoId(response.data.cognitoId))
        }
        if (response.data.isAdmin !== undefined) {
          storage.setUserAdmin(response.data.isAdmin)
          dispatch(setUserAdmin(response.data.isAdmin))
        }
        if (response.data.symphonyUserId) {
          storage.setSymphonyUserId(response.data.symphonyUserId)
          dispatch(setSymphonyUserId(response.data.symphonyUserId))
        }

        dispatch(loginSuccess())

        navigate(redirectUrl)
      } catch (error) {
        console.error('Error exchanging login token:', error)
        const loginUrl =
          redirectUrl !== '/'
            ? `/login?email=${encodeURIComponent(
                email!,
              )}&redirectUrl=${encodeURIComponent(redirectUrl)}`
            : `/login?email=${encodeURIComponent(email!)}`
        navigate(loginUrl)
      }
    }

    exchangeLoginToken()
  }, [searchParams, navigate, dispatch, userId])

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
      }}
    >
      <ClipLoader color="#000000" loading={true} size={50} />
      <p style={{ marginTop: '20px' }}>Authenticating...</p>
    </div>
  )
}

export default LoginToken
